/**
 * dcent-header-pin.js — 모바일 헤더 고정 전환을 **점프 없이** 앞당긴다 (MAGE-601).
 *
 * ── 무엇이 문제였나 ──────────────────────────────────────────────────────────
 * 원본(Layout.tsx + Header.tsx)에는 sticky 전환이 아예 없다. 공지바와 헤더가 처음부터 둘 다
 * `position: fixed` 오버레이이고, 스크롤 8px 을 넘으면 공지바는 위로 슬라이드 아웃하고 헤더는
 * `top` 이 70 → 12 로 300ms 애니메이션한다. 흐름에서 빠진 적이 없으니 잘림도 점프도 없다.
 *
 * 이 테마는 그 겉모습만 이식하고 벤더 Minimog 의 sticky 기구를 그대로 뒀다. 공지바·헤더가
 * 문서 흐름 안에 있고, 벤더 header.js 가 `scrollY > 20 + 공지바높이(71) = 91` 에서 `stuck` 을
 * 붙여 헤더만 fixed 로 바꾼다. 그 지점에서 알약 위치가 **불연속으로 튄다** — 그전 몇 px 구간은
 * 알약 윗부분이 뷰포트 밖으로 잘리기까지 한다. 사용자가 "메뉴가 숨쉬듯 커졌다 작아졌다" 고
 * 본 것이 이것이다(2026-08-30 실측: 알약 치수 287x50 불변, top 만 −2.5 → 8 로 점프).
 *
 * ── 어떻게 고치나 ────────────────────────────────────────────────────────────
 * 벤더보다 **먼저** 자체 클래스(`dcent-hd-pinned`)를 붙인다. 붙이는 지점을 "흐름상 알약 top 이
 * 고정 상태의 알약 top 과 같아지는 순간" 으로 잡으면 전환이 연속이 된다:
 *
 *     흐름상 알약 top(뷰포트) = restTop − scrollY + restPad
 *     고정 시 알약 top        = 0 + pinnedPad
 *     →  임계값 scrollY = restTop − (pinnedPad − restPad)
 *
 * 값을 하드코딩하지 않고 **실측**한다 — 공지바는 문구 길이·표시 여부에 따라 높이가 달라지고,
 * 여백 값도 CSS 가 바꿀 수 있다. 고정 상태에서는 wrapper 가 fixed 라 흐름 위치를 못 재므로
 * **풀린 상태에서만** 다시 잰다.
 *
 * 벤더의 `stuck` 과는 충돌하지 않는다 — 같은 `position: fixed; top: 0` 을 주고, 벤더는 91 에서
 * 자기 클래스를 따로 붙였다 뗀다(멱등). 우리 클래스는 71.5 부터 91 사이의 빈 구간만 메운다.
 *
 * 데스크탑(≥1024)에는 관여하지 않는다 — 그쪽은 공지바 아래 여백이 달라 같은 증상이 없다.
 * JS 가 못 뜨면 종전 동작(벤더 stuck 단독)으로 조용히 되돌아간다 — CSS 가 `.stuck` 에도 같은
 * 값을 갖고 있어 여백 16 은 유지된다.
 *
 * 양 샵 byte-identical — assets/dcent-* 는 parity 게이트 분모다.
 */
(function () {
  var MQ = "(max-width: 1023px)";
  var PINNED = "dcent-hd-pinned";

  function init() {
    var header = document.querySelector(".m-header");
    if (!header) return;
    var wrapper = header.querySelector(".m-header__wrapper");
    var bar = header.querySelector(".m-header__mobile");
    if (!wrapper || !bar) return;

    var mq = window.matchMedia(MQ);
    var restTop = null; // 문서 기준 wrapper 흐름 위치 (풀린 상태에서만 유효)
    var restPad = null; // 평상시 상단 여백 (풀린 상태에서만 유효)
    var pinned = false;

    /** 고정 시 상단 여백 − 평상시 상단 여백.
     *  ⚠️ **상태에 의존하면 안 된다.** 처음엔 pinned 일 때 0 을 돌려줬더니 임계값이 상태에 따라
     *  71.5 ↔ 79.5 로 오가며 클래스가 진동했다(실측: 붙었다 떨어졌다 반복 + 6.4px 점프 잔존).
     *  두 값 모두 **풀린 상태에서 캐시**해 두고 상태와 무관하게 같은 임계값을 낸다. */
    function padDelta() {
      if (restPad === null) return 0;
      var target = parseFloat(
        getComputedStyle(header).getPropertyValue("--dcent-hd-pinned-pt"),
      );
      return isNaN(target) ? 0 : target - restPad;
    }

    function measure() {
      if (pinned) return; // fixed 상태에선 흐름 위치를 못 잰다
      restTop = wrapper.getBoundingClientRect().top + window.scrollY;
      restPad = parseFloat(getComputedStyle(bar).paddingTop) || 0;
    }

    function apply() {
      if (!mq.matches) {
        if (pinned) {
          pinned = false;
          header.classList.remove(PINNED);
        }
        measure();
        return;
      }
      if (restTop === null) measure();
      if (restTop === null) return;
      var threshold = restTop - padDelta();
      var want = window.scrollY >= threshold;
      if (want === pinned) {
        if (!pinned) measure(); // 공지바 높이 변화 추적
        return;
      }
      pinned = want;
      header.classList.toggle(PINNED, want);
      if (!want) measure();
    }

    measure();
    apply();
    window.addEventListener("scroll", apply, { passive: true });
    window.addEventListener("resize", function () {
      if (!pinned) measure();
      apply();
    });
    if (mq.addEventListener) mq.addEventListener("change", apply);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
